import {selectorFamily} from 'recoil';

import {TypeLocalNameEnum} from 'domain/usecases';
import {makeGetExamsTypesLocation, makeGetOdontoSpecialties} from 'main/factories/usecases';
import {makeGetHealthSpecialtiesLocalities} from 'main/factories/usecases/private/booking-flow/remote-health-booking';
import {
	Specialty,
	SpecialtyList,
	categorizeSpecialtiesByFirstLetter,
	sortByKey,
} from 'presentation/utils';
import {formatGender} from 'presentation/utils/formatGender';

import {getCurrentAccountAdapter} from 'main/adapters/current-account-adapter';
import {accountDataStateRefresher} from 'presentation/pages/states';
import {BookingTypeEnum, EntryPointCodeEnum, FlowEnum, TelemedicineEnum} from '../../constants';
import {BookingSpecialtiesParams, ScheduledBooking, ScheduledBookingsParams} from './types';

export const bookingSpecialtiesState = selectorFamily<SpecialtyList[], BookingSpecialtiesParams>({
	key: 'bookingSpecialties',
	get:
		({flowType, cityName, ufCode, typeLocalName}) =>
		async ({get}) => {
			get(accountDataStateRefresher);

			if (!cityName) {
				return [];
			}

			const {
				beneficiary: {flSexoUsuarioC, idadeC},
			} = getCurrentAccountAdapter();

			const isTelehealth = flowType === FlowEnum.TELEHEALTH;
			const genderFormatted = formatGender(flSexoUsuarioC);

			if (flowType === FlowEnum.ODONTO) {
				const response = await makeGetOdontoSpecialties().get({});

				if (!response) {
					return [];
				}

				const specialties = response.especialidades;

				const parsedSpecialties: Specialty<FlowEnum.ODONTO>[] = specialties.flatMap(
					specialty => {
						if (
							specialty?.subEspecialidades?.length === 0 ||
							!specialty?.subEspecialidades
						) {
							return {
								code: specialty.cdEspecialidade.toString(),
								description: specialty.dsEspecialidade,
								telemedicine: specialty.telemedicina,
								formattedText: specialty.dsEspecialidade,
							};
						}
						return specialty?.subEspecialidades?.map(sub => ({
							code: specialty.cdEspecialidade.toString(),
							description: specialty.dsEspecialidade,
							sub_code: sub.cdSubEspecialidade.toString(),
							sub_description: sub.dsSubEspecialidade,
							telemedicine: specialty.telemedicina,
							formattedText: `${specialty.dsEspecialidade} - ${sub.dsSubEspecialidade}`,
						}));
					},
				);
				const sortedSpecialtiesByName =
					parsedSpecialties && sortByKey(parsedSpecialties, 'formattedText');
				const categorizedSpecialties =
					sortedSpecialtiesByName &&
					categorizeSpecialtiesByFirstLetter(sortedSpecialtiesByName);

				return categorizedSpecialties;
			}

			if (flowType === FlowEnum.EXAM) {
				const response = await makeGetExamsTypesLocation().get({
					stateCode: ufCode,
					cityName,
					age: idadeC,
					gender: genderFormatted,
					typeLocalName: typeLocalName || TypeLocalNameEnum.CIDADE,
				});

				if (!response) {
					return [];
				}

				const specialties = response.tiposExames;
				const parsedSpecialties: Specialty<FlowEnum.EXAM>[] = specialties.map(
					specialty => ({
						code: specialty.cdTipoExame,
						description: specialty.dsTipoExame,
						formattedText: specialty.dsTipoExame,
						cdProcedimento: specialty.cdProcedimento,
						dsInformacaoComplementar: specialty.dsInfoComplementar,
						dsPreparo: specialty.dsPreparo,
						dtCarencia: specialty.dtCarencia,
						flAutorizacao: specialty.flAutorizacao,
						flCarencia: specialty.flCarencia,
						flPreparo: specialty.flPreparo,
					}),
				);
				const sortedSpecialtiesByName =
					parsedSpecialties && sortByKey(parsedSpecialties, 'formattedText');
				const categorizedSpecialties =
					sortedSpecialtiesByName &&
					categorizeSpecialtiesByFirstLetter(sortedSpecialtiesByName);

				return categorizedSpecialties;
			}

			const response = await makeGetHealthSpecialtiesLocalities().get({
				stateCode: ufCode,
				cityName,
				entryPointCode: Number(EntryPointCodeEnum.V2),
				telemedicine: isTelehealth ? TelemedicineEnum.IS_TELEMEDICINE : '',
				typeLocalName: typeLocalName || TypeLocalNameEnum.CIDADE,
			});

			if (!response) {
				return [];
			}

			const specialties = response.especialidades;
			const parsedSpecialties: Specialty<FlowEnum.HEALTH>[] = specialties.flatMap(
				specialty => {
					if (specialty.subEspecialidades.length === 0) {
						return {
							code: specialty.cdEspecialidade.toString(),
							description: specialty.dsEspecialidade,
							telemedicine: specialty.telemedicina,
							formattedText: specialty.dsEspecialidade,
							multipleBookings: specialty.flExecutaSessao,
							hasDefinition: specialty.flTemDefinicao,
						};
					}
					return specialty.subEspecialidades.map(sub => ({
						code: specialty.cdEspecialidade.toString(),
						description: specialty.dsEspecialidade,
						sub_code: sub.cdSubEspecialidade.toString(),
						sub_description: sub.dsSubEspecialidade,
						telemedicine: specialty.telemedicina,
						formattedText: `${specialty.dsEspecialidade} - ${sub.dsSubEspecialidade}`,
						multipleBookings: specialty.flExecutaSessao,
						hasDefinition: specialty.flTemDefinicao,
					}));
				},
			);

			const sortedSpecialtiesByName =
				parsedSpecialties && sortByKey(parsedSpecialties, 'formattedText');
			const categorizedSpecialties =
				sortedSpecialtiesByName &&
				categorizeSpecialtiesByFirstLetter(sortedSpecialtiesByName);

			return categorizedSpecialties;
		},
});

export const scheduledBookingState = selectorFamily<
	ScheduledBooking | null,
	ScheduledBookingsParams
>({
	key: 'scheduledBooking',
	get:
		({flowType, specialty_code, appointmentsList}) =>
		async () => {
			if (!specialty_code) {
				return null;
			}
			if (flowType === FlowEnum.ODONTO) {
				const alreadyBooked = appointmentsList.find(
					booking =>
						booking.specialtyCode === specialty_code &&
						booking.bookingType === BookingTypeEnum.ODONTO,
				);

				if (!alreadyBooked) {
					return null;
				}

				return alreadyBooked;
			}

			if (flowType === FlowEnum.EXAM) {
				const alreadyBooked = appointmentsList.find(
					booking =>
						booking.specialtyCode === specialty_code &&
						booking.bookingType === BookingTypeEnum.EXAM,
				);

				if (!alreadyBooked) {
					return null;
				}

				return alreadyBooked;
			}

			const alreadyBooked = appointmentsList.find(
				booking =>
					booking.specialtyCode === specialty_code &&
					(booking.bookingType === BookingTypeEnum.HEALTH ||
						booking.bookingType === BookingTypeEnum.TELEHEALTH),
			);

			if (!alreadyBooked) {
				return null;
			}

			return alreadyBooked;
		},
});
